import React, { useCallback, useContext } from 'react';
import i18next from 'i18next';
import classNames from 'classnames';
import { InView } from 'react-intersection-observer';

import { isLeadPackageAdjacent } from 'lib/packageHelper';
import { BreakpointContext, LayoutContext } from 'lib/ContextTypes';

import VideoPlayer from 'components/VideoPlayer';
import TitleLogo from 'components/TitleLogo';
import PackageTitle from 'components/PackageTitle';

import { Button } from 'components/Button';
import { ListItem } from './ListItem';

import styles from './styles.module.scss';

/**
 * VideoPackage component displays a video package with a video player and a list of videos.
 * Handles video/ad playback, muting, info visibility, and responsive layout.
 *
 * @param {object} props - Component props.
 * @param {import('../../../lib/graphqlTypeDefs.mjs').Package} props.content - Content object containing videos and metadata.
 * @param {number} props.packageIndex - The index of the package in the list.
 */
const VideoPackage = ({
  content,
  packageIndex,
}) => {
  const { isS: isMobile } = useContext(BreakpointContext);
  const { leadPackageType, isFluidWidthPage } = useContext(LayoutContext);

  const [adPlaying, setAdPlaying] = React.useState(false);
  const [currentVideo, setCurrentVideo] = React.useState(content?.items?.[0]?.item);
  const [nowPlayingInitiator, setNowPlayingInitiator] = React.useState('continuous');
  const [videoMuted, setVideoMuted] = React.useState(false);
  const [videoPlaying, setVideoPlaying] = React.useState(false);

  const {
    items: videos,
    id,
    metadata,
  } = (content || {});

  const {
    description,
    logoUrl,
    seeAllText,
    seeAllUrl,
    title,
    shouldAutoplay,
  } = (metadata || {});

  const isLiveVideo = currentVideo?.videoType === i18next.t('Non Broadcast Live Video');
  const isLeadPkgAdjacent = packageIndex === 1 && isLeadPackageAdjacent(leadPackageType);

  const containerClasses = classNames(
    'pkg video-package',
    styles.videoPkg,
    {
      [styles.adPlaying]: adPlaying,
      [styles.isLeadPkgAdj]: isLeadPkgAdjacent,
      [styles.muted]: videoMuted,
    },
  );

  /**
   * Handles when an ad starts playing.
   */
  const onAdPlay = useCallback(() => {
    setAdPlaying(true);
  }, []);

  /**
   * Handles when an ad ends.
   */
  const onAdEnd = useCallback(() => {
    setAdPlaying(false);
  }, []);

  /**
   * Handles muting the video.
   */
  const onVideoMute = useCallback(() => {
    setVideoMuted(true);
  }, []);

  /**
   * Handles unmuting the video.
   */
  const onVideoUnmute = useCallback(() => {
    setVideoMuted(false);
  }, []);

  /**
   * Handles when the video starts playing.
   */
  const onVideoPlay = useCallback(() => {
    setVideoPlaying(true);
  }, []);

  /**
   * Handles when the video is paused.
   */
  const onVideoPause = useCallback(() => {
    setVideoPlaying(false);
  }, []);

  /**
   * Handles when the video ends and selects the next video.
   */
  const onVideoEnd = useCallback(
    (/** @type {import('../../../lib/graphqlTypeDefs.mjs').Tease} */ selected) => {
      setNowPlayingInitiator('continuous');
      setCurrentVideo(selected.item);
    },
    [],
  );

  /**
   * Changes the current video.
   */
  const changeVideo = useCallback(
    (/** @type {import('../../../lib/graphqlTypeDefs.mjs').Video} */ item) => {
      if (isMobile) {
        window.location.href = item.url.primary;
        return;
      }

      setNowPlayingInitiator('manual');
      setCurrentVideo(item);
      setVideoPlaying(true);
    },
    [isMobile, videoPlaying, currentVideo],
  );

  const seeAllTextProp = i18next.t(seeAllText) || i18next.t('See All');

  return currentVideo && (
    <section
      className={containerClasses}
      data-packageid={id}
      data-testid="video-package__section"
    >
      {isFluidWidthPage && (
        <div className="grid-col-push-1-m grid-col-10-m fluidWidth">
          <PackageTitle
            metadata={{ title, description, logoUrl }}
            className="video-pkg-title"
          />
        </div>
      )}
      <div className={styles.grid}>
        <div className={styles.columnVideo}>
          {!isFluidWidthPage && (
            <div
              className={styles.titleContainer}
              data-testid="video-package__header"
            >
              <h2 className={styles.titleText}>
                <TitleLogo logoUrl={logoUrl} title={title} />
              </h2>
              {description && (
                <p className={styles.titleDescription}>
                  {description}
                </p>
              )}
            </div>
          )}
          <div
            className={styles.videoOuter}
          >
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div className={styles.videoInner} ref={ref}>
                  {shouldAutoplay && inView ? (
                    <VideoPlayer
                      adPlayButtonPosition="bottomLeft"
                      autoPlay
                      continuousPlay
                      isLive={isLiveVideo}
                      nowPlayingInitiator={nowPlayingInitiator}
                      onAdEnd={onAdEnd}
                      onAdPlay={onAdPlay}
                      onVideoEnd={onVideoEnd}
                      onVideoMute={onVideoMute}
                      onVideoPause={onVideoPause}
                      onVideoPlay={onVideoPlay}
                      onVideoUnmute={onVideoUnmute}
                      playlist={[{ videos }]}
                      video={currentVideo}
                    />
                  ) : (
                    <VideoPlayer
                      adPlayButtonPosition="bottomLeft"
                      continuousPlay
                      isLive={isLiveVideo}
                      onAdEnd={onAdEnd}
                      onAdPlay={onAdPlay}
                      nowPlayingInitiator={nowPlayingInitiator}
                      onVideoEnd={onVideoEnd}
                      onVideoMute={onVideoMute}
                      onVideoPause={onVideoPause}
                      onVideoPlay={onVideoPlay}
                      onVideoUnmute={onVideoUnmute}
                      playlist={[{ videos }]}
                      video={currentVideo}
                    />
                  )}
                </div>
              )}
            </InView>
          </div>
        </div>
        <div className={classNames(styles.columnList, { [styles.noSeeAll]: !seeAllUrl })}>
          <div className={styles.scroll}>
            <div className={styles.list}>
              {videos.filter(Boolean).map((item) => (
                <ListItem
                  content={item}
                  key={item.id}
                  changeVideo={changeVideo}
                  playing={videoPlaying}
                  active={item.id === currentVideo.id}
                  isRebrandFront={isFluidWidthPage}
                />
              ))}
            </div>
          </div>
          {seeAllUrl && (
            <div className={
              classNames({ [styles.seeAllWrapper]: !isFluidWidthPage }, styles.buttonWrapper)
            }
            >
              <Button
                url={seeAllUrl}
                title={seeAllTextProp}
                additionalClasses={styles.seeAllBtn}
                tiltClasses={styles.buttonTilt}
                type="link"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export { VideoPackage };
